import { DATE_PIPE_DEFAULT_OPTIONS } from "@angular/common";
import { HttpBackend, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorHandler, NgModule, NgZone } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router, RouterModule } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { provideTanStackQuery, QueryClient } from "@tanstack/angular-query-experimental";
import { withDevtools } from "@tanstack/angular-query-experimental/devtools";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";

import { provideAppFeatures } from "@intocare/app-utils/lib/app-feature";
import { AuthModule } from "@intocare/auth";
import { WindowRef } from "@intocare/auth-data";
import { AWSHttpModule, S3_IMAGE_CONFIG } from "@intocare/aws";
import { CoreModule as SharedCoreModule, UploadService } from "@intocare/core";
import { COMMUNICATION_DATA_API_PREFIX } from "@intocare/data-access/shared/communication";
import { provideDesignSystem } from "@intocare/design/design-system-core";
import { ENVIRONMENT_TOKEN, homeEnvironment } from "@intocare/environments";
import { CustomSentryErrorHandler } from "@intocare/errors";
import {
	CacheBustMultiTranslateHttpLoader,
	CURRENT_LANGUAGE,
	LanguageModule,
	SupportedLanguage,
} from "@intocare/language";
import { configureBufferSize, handleUndo } from "@intocare/ngrx-undo";
import { OrganisationModule } from "@intocare/organisation/data";
import { ResidentsDataModule } from "@intocare/residents/data";
import { FileUploadService } from "@intocare/ui";
import { CONTEXT_TOKEN, DATE_FORMAT } from "@intocare/util";

import { AppComponent } from "./app.component";
import { routes } from "./app.routing";
import { CoreModule } from "./core";
import { ContextService } from "./core/services/context.service";

// Ensure locale data is bundled for correct locale display values, including moment.
// TODO: Improve including locale data, so it's also correctly bundled in unit tests.
//		Preferably, we use the browser APIs so we don't have to include any locale data.
//		See https://intocare.atlassian.net/browse/ICP-18319.
import "@angular/common/locales/global/nl-BE";
import "@angular/common/locales/global/fr-BE";
// TODO: Work away the need for moment locales by using Angular's date pipe or Intl APIs.
//		We could use some things from [ngxtension](https://ngxtension.dev/utilities/intl/intl/).
//		See https://intocare.atlassian.net/browse/ICP-18314.
import "moment";
import "moment/locale/nl-be";
import "moment/locale/fr";

export function translateLoaderFactory(httpBackend: HttpBackend) {
	return new CacheBustMultiTranslateHttpLoader(httpBackend, [
		"assets/modules/wound-care/i18n/",
		"assets/modules/residents/i18n/",
		"assets/modules/external-staff/i18n/",
		"assets/shared/i18n/",
		"assets/modules/wizards/i18n/",
		"assets/i18n/",
		"assets/modules/medication-order/data/i18n/",
		"assets/modules/medication/tasks/i18n/",
		"assets/modules/medication/core/i18n/",
		"assets/modules/users-data/i18n/",
		"assets/modules/care-registrations-data/i18n/",
		"assets/modules/residents-data/i18n/",
		"assets/modules/indicators-data/i18n/",
		"assets/modules/care-registrations-data/i18n/",
		"assets/modules/history-data/i18n/",
		"assets/modules/medication-data/i18n/",
		"assets/@intocare/domain-l10n/domain-model/",
		"assets/@intocare/domain-l10n/domain-codes/",
		"assets/design/design-system-base/i18n/",
	]);
}

export function windowFactory() {
	return window;
}

export function awsHttpConfigFactory() {
	return {
		region: "eu-west-1",
		apiKey: homeEnvironment.API.key,
	};
}

export function s3ImageFactory() {
	return {
		bucket: homeEnvironment.AWS.bucket,
	};
}

export function currentLanguageFactory(translateService: TranslateService) {
	return translateService.getCurrentLang() as SupportedLanguage;
}

configureBufferSize(20);

const devtools = homeEnvironment.production ? [] : StoreDevtoolsModule.instrument({ connectInZone: true });

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AWSHttpModule.forRoot(awsHttpConfigFactory),
		RouterModule.forRoot(routes, {
			bindToComponentInputs: true,
			paramsInheritanceStrategy: "always",
			canceledNavigationResolution: "computed",
		}),
		CoreModule.forRoot(),
		SharedCoreModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: translateLoaderFactory,
				deps: [HttpBackend],
			},
		}),
		StoreModule.forRoot(
			{},
			{
				metaReducers: [handleUndo],
				runtimeChecks: {
					strictStateImmutability: false,
					strictActionImmutability: false,
				},
			},
		),
		devtools,
		AuthModule,
		LanguageModule.forRoot(),
		OrganisationModule,
		ResidentsDataModule,
		ServiceWorkerModule.register("ngsw-worker.js", {
			enabled: homeEnvironment.PWA,
		}),
		MatButtonModule,
		ToastModule,
		ConfirmDialogModule,
	],
	providers: [
		{ provide: WindowRef, useFactory: windowFactory },
		{ provide: S3_IMAGE_CONFIG, useFactory: s3ImageFactory },
		{
			provide: ErrorHandler,
			useClass: CustomSentryErrorHandler,
			deps: [NgZone, Router],
		},
		{ provide: FileUploadService, useClass: UploadService },
		{
			provide: CURRENT_LANGUAGE,
			deps: [TranslateService],
			useFactory: currentLanguageFactory,
		},
		{ provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_FORMAT.DISPLAY_MEDIUM } },
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: {
				disableTooltipInteractivity: true,
			},
		},
		{
			provide: ENVIRONMENT_TOKEN,
			useValue: homeEnvironment,
		},
		provideAppFeatures(homeEnvironment.APP_FEATURES ?? {}),
		{ provide: COMMUNICATION_DATA_API_PREFIX, useValue: "/communication/portal" },
		provideTanStackQuery(
			new QueryClient({
				defaultOptions: {
					queries: { retry: false, throwOnError: true },
					mutations: { retry: false, throwOnError: true },
				},
			}),
			withDevtools(),
		),
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: CONTEXT_TOKEN, useExisting: ContextService },
		...provideDesignSystem(),
	],
})
export class AppModule {}
